import { Button, Typography, Paper, Stack, TextField } from '@mui/material';
const statusLabels = {
  calm: 'רגוע',
  medium: 'בינוני',
  busy: 'עמוס',
  very_busy: 'עמוס מאוד',
};
const statusForScore = (score) =>
  score <= 4 ? 'calm' : score <= 6 ? 'medium' : score <= 8 ? 'busy' : 'very_busy';
const simulate = (behavior, scenario) => {
  const waitingPercent = Number(scenario.waitingPercent);
  const durationMinutes = Number(scenario.durationMinutes);
  const walkingPercent = Number(scenario.walkingPercent || 0);
  const matchedIndex = (behavior.rules || []).findIndex(
    (rule) =>
      waitingPercent >= Number(rule.minWaitingPercent) &&
      durationMinutes >= Number(rule.minDurationMinutes),
  );
  if (matchedIndex < 0) return null;
  const matchedRule = behavior.rules[matchedIndex];
  let score = Number(matchedRule.score);
  (behavior.boosts || []).forEach((boost) => {
    if (
      waitingPercent >= Number(boost.minWaitingPercent) &&
      walkingPercent >= Number(boost.minWalkingPercent)
    ) {
      score = Math.max(score, Number(boost.minimumScore));
    }
  });
  return {
    matchedIndex,
    score,
    status: statusForScore(score),
    reason: matchedRule.reason,
  };
};
const SimulationModal = ({ behavior, onChange, onClose }) => {
  const scenarios = behavior.simulationScenarios || [];
  const updateScenario = (index, patch) => {
    onChange(
      scenarios.map((scenario, scenarioIndex) =>
        scenarioIndex === index
          ? {
              ...scenario,
              ...patch,
            }
          : scenario,
      ),
    );
  };
  const addScenario = () => {
    onChange([
      ...scenarios,
      {
        id: `scenario-${Date.now()}`,
        name: `תרחיש ${scenarios.length + 1}`,
        waitingPercent: 0,
        durationMinutes: 5,
        walkingPercent: 0,
      },
    ]);
  };
  return (
    <Stack
      onMouseDown={onClose}
      sx={(theme) => ({
        position: 'fixed',
        inset: '0',
        zIndex: '1200',
        display: 'grid',
        placeItems: 'center',
        padding: '24px',
        background: 'rgba(21, 33, 31, .56)',
        [theme.breakpoints.down('sm')]: {
          padding: '0',
        },
        backdropFilter: 'blur(6px)',
      })}
    >
      <Stack
        dir="rtl"
        onMouseDown={(event) => event.stopPropagation()}
        sx={(theme) => ({
          width: 'min(1280px, 96vw)',
          height: 'calc(100vh - 48px)',
          maxHeight: '92vh',
          minHeight: '0',
          display: 'grid',
          gridTemplateRows: 'auto auto minmax(0, 1fr) auto',
          contain: 'layout',
          background: 'var(--panel)',
          borderRadius: '20px',
          boxShadow: 'var(--shadow-lg)',
          overflow: 'hidden',
          textAlign: 'right',
          [theme.breakpoints.down('sm')]: {
            width: '100%',
            height: '100vh',
            maxHeight: '100vh',
            minHeight: '100vh',
            borderRadius: '0',
          },
          border: '1px solid rgba(255,255,255,.45)',
        })}
      >
        <Stack
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            padding: '20px 24px',
            borderBottom: '1px solid var(--line)',
            [theme.breakpoints.down('sm')]: {
              padding: '16px',
            },
          })}
        >
          <Stack>
            <Typography>סימולציית עומס — {behavior.label}</Typography>
            <Typography>החישוב משתמש בתנאים ובסדר שמופיעים כרגע בטיוטה</Typography>
          </Stack>
          <Button
            type="button"
            onClick={onClose}
            sx={{
              width: '42px',
              height: '42px',
              minHeight: '42px',
              borderRadius: '50%',
              padding: '0',
              fontSize: '28px',
              lineHeight: '1',
              background: '#eef2f6',
              color: 'var(--text)',
            }}
          >
            ×
          </Button>
        </Stack>

        <Paper
          sx={(theme) => ({
            minWidth: '0',
            minHeight: '0',
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            scrollbarGutter: 'stable',
            padding: '15px',
            display: 'grid',
            alignContent: 'start',
            gap: '24px',
            [theme.breakpoints.down('sm')]: {
              padding: '14px',
            },
            overflow: 'auto',
          })}
        >
          <Stack
            sx={{
              display: 'grid',
              gap: '8px',
              minWidth: '1080px',
            }}
          >
            <Stack
              sx={{
                display: 'grid',
                gridTemplateColumns:
                  'minmax(180px, 1.2fr) 110px 100px 110px minmax(220px, 1.3fr) 150px 80px',
                gap: '8px',
                alignItems: 'center',
                color: 'var(--muted)',
                fontSize: '12px',
                fontWeight: '800',
                padding: '0 4px',
              }}
            >
              <Typography>שם התרחיש</Typography>
              <Typography>המתנה %</Typography>
              <Typography>דקות</Typography>
              <Typography>הליכה %</Typography>
              <Typography>תנאי שנתפס</Typography>
              <Typography>תוצאה</Typography>
              <Typography />
            </Stack>
            {scenarios.map((scenario, index) => {
              const result = simulate(behavior, scenario);
              return (
                <Stack
                  key={scenario.id || index}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns:
                      'minmax(180px, 1.2fr) 110px 100px 110px minmax(220px, 1.3fr) 150px 80px',
                    gap: '8px',
                    alignItems: 'center',
                    padding: '8px',
                    border: '1px solid var(--line)',
                    borderRadius: '12px',
                    background: '#f8fafc',
                  }}
                >
                  <TextField
                    value={scenario.name}
                    onChange={(event) =>
                      updateScenario(index, {
                        name: event.target.value,
                      })
                    }
                    fullWidth
                  />
                  <TextField
                    type="number"
                    min="0"
                    max="100"
                    value={scenario.waitingPercent}
                    onChange={(event) =>
                      updateScenario(index, {
                        waitingPercent: Number(event.target.value),
                      })
                    }
                    fullWidth
                  />
                  <TextField
                    type="number"
                    min="0"
                    value={scenario.durationMinutes}
                    onChange={(event) =>
                      updateScenario(index, {
                        durationMinutes: Number(event.target.value),
                      })
                    }
                    fullWidth
                  />
                  <TextField
                    type="number"
                    min="0"
                    max="100"
                    value={scenario.walkingPercent || 0}
                    onChange={(event) =>
                      updateScenario(index, {
                        walkingPercent: Number(event.target.value),
                      })
                    }
                    fullWidth
                  />
                  <Typography
                    sx={{
                      fontSize: '12px',
                      direction: 'ltr',
                      textAlign: 'right',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {result
                      ? `#${result.matchedIndex + 1} · ${result.reason || 'ללא סיבה'}`
                      : 'אף תנאי'}
                  </Typography>
                  {result ? (
                    <Typography
                      sx={String(`rule-status rule-status-${result.status}`)
                        .split(/\s+/)
                        .map(
                          (name) =>
                            ({
                              'rule-status': {
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '36px',
                                padding: '5px 10px',
                                borderRadius: '999px',
                                fontSize: '13px',
                                fontWeight: '800',
                              },
                              'rule-status-calm': {
                                background: '#dcfae6',
                                color: '#067647',
                              },
                              'rule-status-medium': {
                                background: '#fff6d6',
                                color: '#b54708',
                              },
                              'rule-status-busy': {
                                background: '#ffead5',
                                color: '#c4320a',
                              },
                              'rule-status-very_busy': {
                                background: '#fee4e2',
                                color: '#b42318',
                              },
                            })[name],
                        )
                        .filter(Boolean)}
                    >
                      {result.score} · {statusLabels[result.status]}
                    </Typography>
                  ) : (
                    <Typography
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '36px',
                        borderRadius: '999px',
                        background: '#f2f4f7',
                        color: '#667085',
                        fontWeight: '800',
                      }}
                    >
                      אין ציון
                    </Typography>
                  )}
                  <Button
                    type="button"
                    onClick={() =>
                      onChange(scenarios.filter((_, scenarioIndex) => scenarioIndex !== index))
                    }
                    sx={{
                      color: '#b42318',
                      borderColor: '#f1b9b4',
                      background: '#fff5f4',
                    }}
                  >
                    הסרה
                  </Button>
                </Stack>
              );
            })}
          </Stack>
          {!scenarios.length && (
            <Typography
              sx={(theme) => ({
                color: 'var(--muted)',
                [theme.breakpoints.down('lg')]: {
                  margin: '4px 0 0',
                  fontSize: '12px',
                  display: 'none',
                },
                margin: '2px 0 0',
                fontSize: '11px',
              })}
            >
              אין תרחישים. אפשר להוסיף תרחיש חדש.
            </Typography>
          )}
        </Paper>

        <Stack
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '16px',
            padding: '20px 24px',
            borderBottom: '0',
            borderTop: '1px solid var(--line)',
            [theme.breakpoints.down('sm')]: {
              padding: '16px',
              position: 'sticky',
              bottom: '0',
              flexWrap: 'wrap',
              background: '#fff',
              '& button': {
                flex: '1 1 120px',
              },
            },
          })}
        >
          <Typography
            sx={(theme) => ({
              color: 'var(--muted)',
              [theme.breakpoints.down('lg')]: {
                margin: '4px 0 0',
                fontSize: '12px',
                display: 'none',
              },
              margin: '2px 0 0',
              fontSize: '11px',
            })}
          >
            התרחישים נשמרים בלחיצה על „שמירת הגדרות” במסך הראשי.
          </Typography>
          <Typography
            sx={{
              flex: '1',
            }}
          />
          <Button
            type="button"
            onClick={addScenario}
            sx={{
              background: '#edf2f7',
              color: 'var(--text)',
              '&:hover': {
                background: '#ddebff',
                color: '#1269e8',
              },
            }}
          >
            הוספת תרחיש
          </Button>
          <Button type="button" onClick={onClose}>
            סיום
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
export default SimulationModal;
